import type { FileEntry } from 'bz-block-template/components/file-upload/useFileUploadState';
import { action, computed, observable } from 'mobx';
import React from 'react';
import { ContentState, convertToRaw, EditorState, Modifier } from 'tm2-draft-js';
import Control from '../draft/entities/control';
import BaseStore from './base-store';
import TemplateStore from './template-store';

class ReviewStore extends BaseStore {
  @observable
  mode = 'live';

  @observable
  lists = {};

  @observable
  lookups = {};

  @observable
  searches = {};

  @observable
  formId: string | undefined;

  // Stubs to mirror FormStore's file-upload surface. ReviewStore backs the read-only print/review
  // view where uploads cannot happen, but DraftFileUpload still calls these methods unconditionally
  // via the shared `draftStore` provider.
  @computed
  get hasPendingFiles(): boolean {
    return false;
  }

  public getFileEntries(_fieldId: string): FileEntry[] {
    return [];
  }

  @action.bound
  addFileEntry(_fieldId: string, _entry: FileEntry) {}

  @action.bound
  updateFileEntry(_fieldId: string, _key: string, _patch: Partial<FileEntry>) {}

  @action.bound
  removeFileEntry(_fieldId: string, _key: string) {}

  @action.bound
  clearFileEntries(_fieldId: string) {}

  @action
  load(template, data) {
    this.editor = TemplateStore.load(template, this.decoratorFn());
    if (!!data) {
      // this is being done to fix incompatability with base store computed
      this.dataAsMap.replace(data);
    }
    this.updateTemplate();
  }

  @action
  onExport() {
    return convertToRaw(this.editor.getCurrentContent());
  }

  @action
  loadBlocks(blocks, data) {
    this.dataAsMap.replace(data || {});
    blocks = blocks || [];

    this.editor =
      blocks.length === 0
        ? EditorState.createEmpty()
        : EditorState.createWithContent(
            ContentState.createFromBlockArray(blocks),
            this.decoratorFn()
          );
  }

  @action
  loadString(json, data) {
    this.dataAsMap.replace(data || {});
    this.editor = EditorState.createWithContent(
      ContentState.createFromText(json),
      this.decoratorFn()
    );
  }

  @action
  onExtract() {
    let blocks = [];
    let content = this.editor.getCurrentContent();

    content.getBlocksAsArray().map((block) => {
      let range = [];
      block.findStyleRanges(
        (x) => {
          return x.hasStyle('SUMMARY') || x.hasStyle('HIGHLIGHT');
        },
        (start, end) => {
          let last = range.length - 1;
          if (range[last] === start) {
            range[last] = end;
          } else {
            range.push(start, end);
          }
        }
      );
      if (range.length > 0) {
        blocks.push(block);
      }
    });

    return blocks;
  }

  onMerge(highlight) {
    let content = this.editor.getCurrentContent();
    let selection = this.editor.getSelection();
    let currentBlockKeys = this.editor
      .getCurrentContent()
      .getBlockMap()
      .toArray()
      .map((x) => x.getKey());
    Object.keys(highlight || {})
      .filter((x) => currentBlockKeys.indexOf(x) >= 0) // prevent errors with groups..?
      .map((x) => {
        for (let i = 0; i < highlight[x].length; i += 2) {
          selection = selection.merge({
            anchorKey: x,
            focusKey: x,
            anchorOffset: highlight[x][i],
            focusOffset: highlight[x][i + 1]
          });

          content = Modifier.applyInlineStyle(content, selection, 'HIGHLIGHT');
        }
      });

    this.editor = EditorState.push(this.editor, content, 'change-inline-style');
  }

  controlFn(props) {
    return (
      <Control
        lists={this.lists}
        cache={this.cache}
        lookups={this.lookups}
        searches={this.searches}
        control={props.control}
        children={props.children}
        attributes={props.attributes}
        inline={props.inline || false}
        value={this.data[props.field]}
        valueOther={this.data[props.field + '-explanation']}
        readOnly={true}
        psfs={this.psfs}
        formId={this.formId}
      />
    );
  }
}

export default ReviewStore;
